import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import Portal from "../Portal";
import Button from "../Button";
import PickerItem from "./PickerItem";
import { equals, getOtherProperties } from "../common/Utils";
import { FadeInUp, Fade } from "../Transitions";
import { PickerProps, PickerState } from "./PropsType";

export default function Picker(props: PickerProps) {
  const lastInputValue = useRef([]);
  const bodyOverflow = useRef('');
  const { className, format = "/", ...other } = props;
  const [state, setState] = useState<PickerState>({
    data: props.data || [],
    inputValue: "",
    value: [],
    visible: false,
  });
  const { data, inputValue, value, visible }: any = state;
  const styleClass = classNames(
    "Picker-panel",
    {
      "Picker-open": visible,
    },
    className
  );
  const otherProps: object = getOtherProperties(other, [
    "defaultValue",
    "children",
    "data",
    "format",
    "onChange",
    "onOpen",
    "onValueChange",
    "visible",
  ]);
  const getDefaultValue = (data: any[], defaultValue: any) => {
    let value: any[] = [];
    if (Array.isArray(defaultValue)) {
      value = defaultValue;
    } else if (typeof defaultValue === "string") {
      value = (defaultValue as any).split(props.format!).map((v: any) => ({
        value: v,
      }));
    } else {
      let child: any = [...data];
      while (child && child.length > 0) {
        value.push({
          label: child[0].label,
          value: child[0].value,
        });
        child = child[0] && child[0].children;
      }
    }
    return value;
  };

  const getCurrentIndex = (data: any, selectedValue: any) => {
    const value: any = [];
    if (selectedValue) {
      let child: any = [...data!];
      selectedValue.map((v: any) => {
        if (child && child.length > 0) {
          for (let i = 0; i < child.length; i++) {
            if (equals(child[i], v, "value")) {
              child = child[i] && child[i].children;
              value.push(i);
              break;
            }
          }
        }
      });
    }
    return value;
  };

  const getItems = (data: any, value: any) => {
    const children: any[] = [];
    if (data && data.length > 0) {
      let num = 0;
      let child: any = [...data!];
      while (child && child.length > 0) {
        const index: number = (value as any[])[num] || 0;
        children.push({ num, index, child });
        child = child[index] && child[index].children!;
        num++;
      }
      for (let i = 0; i < children.length; i++) {
        const childData: any = children[i];
        children[i] = (
          <PickerItem
            key={childData.num}
            index={childData.num}
            data={childData.child}
            style={{ width: 100 / children.length + "%" }}
            disabled={props.disabled}
            value={childData.index}
            onValueChange={handleValueChange}
          />
        );
      }
    }
    return children;
  };

  const fetchValue = (
    data: any,
    value: Array<number>,
    field: string,
    seperator: string = "/"
  ) => {
    const selected: any = [];
    if (Array.isArray(data) && value) {
      let child: any = data;
      let i = 0;
      while (child && child.length > 0) {
        child = child[value[i] || 0];
        if (child) {
          selected.push({
            label: child.label,
            value: child.value,
          });
        }
        i++;
        child = child && child.children;
      }
    }
    return selected
      .map((item: any) => {
        return item[field];
      })
      .join(seperator);
  };

  const handleClose = () => {
    setState({
      ...state,
      value: lastInputValue.current,
      visible: false,
    });
    if (props.onClose) {
      props.onClose();
    }
  };

  const handleValueChange = (event: any) => {
    const value: any = [...state.value!];
    value[event.index] = event.activeIndex;
    setState({
      ...state,
      value,
    });
    if (props.onValueChange) {
      props.onValueChange({
        value: event.value,
        index: event.index,
      });
    }
  };

  const handleConfirm = () => {
    const value: any = [...state.value!];
    const inputValue = fetchValue(state.data, value, "label");
    setState({
      ...state,
      value,
      inputValue,
      visible: false,
    });
    lastInputValue.current = value;
    if (props.onClose) {
      props.onClose();
    }
    if (props.onChange) {
      props.onChange(inputValue);
    }
  };

  const stopBodyScroll = (visible: boolean) => {
    if (bodyOverflow.current === "hidden") {
      return;
    }
    if (visible) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = bodyOverflow.current;
    }
  };

  const handleClick = () => {
    setState({
      ...state,
      visible: true,
    });
    stopBodyScroll(true);
    if (props.onOpen) {
      (props.onOpen as () => void)();
    }
  };

  useEffect(() => {
    bodyOverflow.current = document.body.style.overflow;
    const { data, defaultValue }: any = props;
    if (Array.isArray(props.data!) && data.length > 0) {
      const value: any = defaultValue
        ? getCurrentIndex(data!, getDefaultValue(data!, defaultValue!))
        : [];
      setState({
        ...state,
        data,
        value,
        inputValue: defaultValue && fetchValue(data, defaultValue, "label"),
      });
      lastInputValue.current = value;
    }
  }, []);

  useEffect(() => {
    const dValue = props.value;
    let value: any = props.value
      ? getCurrentIndex(props.data!, getDefaultValue(props.data!, dValue))
      : state.value!;
    if (!props.visible || !state.visible) {
      value = lastInputValue.current;
    }
    setState({
      ...state,
      visible: props.visible!,
      value,
      data: props.data!,
    });
    stopBodyScroll(!!props.visible);
  }, [props.visible, props.data, props.value]);

  return (
    <div className="Picker">
      <div className="Picker-input" onClick={handleClick}>
        {inputValue}
      </div>
      <Portal visible={visible} mountNode={document.body}>
        <div className={styleClass} {...otherProps}>
          <Fade in={visible}>
            <div className="Picker-mask" onClick={handleClose} />
          </Fade>
          <FadeInUp in={visible} className="Picker-wrapper">
            <div className="Picker-footer">
              <Button onClick={handleClose}>Cancel</Button>
              <Button onClick={handleConfirm}>Done</Button>
            </div>
            <div className="Picker-body">
              <div className="Picker-top-overlay" />
              <div className="Picker-bottom-overlay" />
              <div className="Picker-line" />
              {getItems(data, value)}
            </div>
          </FadeInUp>
        </div>
      </Portal>
    </div>
  );
}
